import React, { ImgHTMLAttributes, useEffect } from 'react';
import Card from '../Card/Card';
import Badge from '#/components/Form/Badge';
import { TRootState } from '#/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import {
  createActionLibraryErrorAlbums,
  createActionLibraryErrorArtists,
  createActionLibraryErrorPlaylists,
  createActionLibraryLoadingAlbums,
  createActionLibraryLoadingArtists,
  createActionLibraryLoadingPlaylists,
  createActionLibrarySetAlbums,
  createActionLibrarySetArtists,
  createActionLibrarySetPlaylists,
  createActionLibrarySetSelected,
} from '#/redux/actions/library';
import ApiManager from '#/services/api/ApiManager';
import { spotifyApiConfig } from '#/utils/constants';
import { IMyAlbumsResponse } from '#/types/API/myAlbums';
import { IMyArtistsResponse } from '#/types/API/MyArtists';
import { IMyPlaylistsResponse } from '#/types/API/myPlaylists';
import { TLibrarySelected } from '#/redux/reducers/library';
import useProfile from '#/hooks/useProfile';
import favorite from '#/assets/img/favorite.png';
import { Link } from 'react-router-dom';
import { ROUTE_NAMES } from '#/router/routes';
interface IArtistLibraryItemProperties {
  image: ImgHTMLAttributes<HTMLImageElement>['src'];
  name: string;
  subname?: string;
  type: TLibrarySelected | 'favorite';
  id?: string;
}

const LibraryItem = ({
  image,
  name,
  type,
  subname,
  id,
}: IArtistLibraryItemProperties) => {
  const grayText = 'text-sm text-theme-light-gray line-clamp-1';
  return (
    <Link
      to={
        type === 'artists'
          ? `${ROUTE_NAMES.ARTIST}/${id}`
          : type === 'albums'
          ? `${ROUTE_NAMES.ALBUM}/${id}`
          : type === 'playlists'
          ? `${ROUTE_NAMES.PLAYLIST}/${id}`
          : ROUTE_NAMES.HOME
      }
    >
      <div
        className={
          'flex gap-2 h-16 p-2 rounded-md hover:bg-theme-dark-gray cursor-pointer'
        }
      >
        <div
          className={` aspect-square overflow-hidden ${
            type === 'artists' ? 'rounded-full' : 'rounded-md'
          }`}
        >
          <img className=" object-cover h-full w-full" src={image} alt="" />
        </div>
        <div className="flex flex-col">
          <span className="text-base text-theme-white line-clamp-1">
            {name}
          </span>
          <div className="flex gap-1 items-center">
            <span className={grayText}>
              {type === 'artists'
                ? 'Artist'
                : type === 'albums'
                ? 'Album'
                : 'Playlist'}
            </span>
            {type !== 'artists' && (
              <>
                •<span className={grayText}>{subname}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

const librarySelector = (state: TRootState) => state.library;

const LibraryBar = () => {
  const profile = useProfile();

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const librarySlice = useSelector(librarySelector);

  const { albums, artists, playlists, selected } = librarySlice;

  useEffect(() => {
    const getMyAlbums = async () => {
      dispatch(createActionLibraryLoadingAlbums());
      const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

      const response = await apiManager.getAllEntities<IMyAlbumsResponse>(
        'myAlbums',
        { limit: 50 }
      );

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionLibraryErrorAlbums());
      } else {
        dispatch(createActionLibrarySetAlbums(response.data.items));
      }
    };

    const getMyArtists = async () => {
      dispatch(createActionLibraryLoadingArtists());
      const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

      const response = await apiManager.getAllEntities<IMyArtistsResponse>(
        'myArtists',
        { limit: 50, type: 'artist' }
      );

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionLibraryErrorArtists());
      } else {
        dispatch(createActionLibrarySetArtists(response.data.artists.items));
      }
    };

    const getMyPlaylists = async () => {
      dispatch(createActionLibraryLoadingPlaylists());
      const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

      const response = await apiManager.getAllEntities<IMyPlaylistsResponse>(
        'myPlaylists',
        { limit: 50 }
      );

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionLibraryErrorPlaylists());
      } else {
        dispatch(createActionLibrarySetPlaylists(response.data.items));
      }
    };

    getMyAlbums();
    getMyArtists();
    getMyPlaylists();
  }, [dispatch, protectedSpotify]);

  return (
    <Card
      className={`flex flex-col flex-grow min-h-full max-h-full overflow-hidden`}
    >
      <div className="px-4 pt-2 text-theme-light-gray">
        <span className="pt-2 pb-4 px-2 flex items-center gap-3 font-bold">
          <span className="material-icons">collections_bookmark</span>Your
          library
        </span>
        <div className="h-12 flex items-center gap-2 text-theme-white">
          <Badge
            text="Artists"
            selected={selected === 'artists'}
            onClick={() => dispatch(createActionLibrarySetSelected('artists'))}
          />
          <Badge
            text="Albums"
            selected={selected === 'albums'}
            onClick={() => dispatch(createActionLibrarySetSelected('albums'))}
          />
          <Badge
            text="Playlists"
            selected={selected === 'playlists'}
            onClick={() =>
              dispatch(createActionLibrarySetSelected('playlists'))
            }
          />
        </div>
      </div>
      <div className=" p-2 flex-1 min-h-0 h-full scrollable">
        <div className="flex-1 gap-2 flex flex-col text-theme-light-gray">
          {selected === 'artists' &&
            Array.isArray(artists) &&
            artists.map((artist) => {
              return (
                <LibraryItem
                  type="artists"
                  id={artist.id}
                  key={artist.id}
                  image={artist.images[2].url}
                  name={artist.name}
                />
              );
            })}
          {selected === 'albums' &&
            Array.isArray(albums) &&
            albums.map((album) => (
              <LibraryItem
                id={album.album.id}
                key={album.album.id}
                type="albums"
                name={album.album.name}
                subname={album.album.artists[0].name}
                image={album.album.images[2].url}
              />
            ))}
          {selected === 'playlists' && Array.isArray(playlists) && (
            <>
              <LibraryItem
                type="favorite"
                name="Liked Songs"
                subname={profile?.display_name}
                image={favorite}
              />
              {playlists.map((playlist) => (
                <LibraryItem
                  id={playlist.id}
                  key={playlist.id}
                  type="playlists"
                  name={playlist.name}
                  subname={playlist.owner.display_name}
                  image={playlist.images[0].url}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default LibraryBar;
